import { useCallback, useMemo, useState } from "react";

import type { APILibraryTemplatePackResponseType } from "@/core/data_sources/models/APILibraryTemplatePack";
import { AppStore } from "@/core/data_sources/store/app";
import { Button } from "@/core/ui/atoms";
import {
  adminInputClasses,
  adminSelectClasses,
  AdminTableToolbar,
  Banner,
  DataTable,
  PageHeader,
} from "@/core/ui/molecules";
import { DialogTemplate } from "@/core/ui/templates";
import { useStore } from "@/core/utils/hooks";

import { ChipList, StatusBadge } from "../../components/molecules";
import type { CatalogMode } from "../../utils";
import { LibraryTemplatesStore } from "../templates/data_sources/store";
import { LibraryVerticalsStore } from "../verticals/data_sources/store";
import { CreateTemplatePackDialog } from "./components/CreateTemplatePackDialog";
import { TemplatePackDetailDrawer } from "./components/TemplatePackDetailDrawer";
import { LibraryTemplatePacksStore } from "./data_sources/store";

type PackStatusFilter = "all" | "active" | "disabled";

export function TemplatePacksPage({ mode }: { readonly mode: CatalogMode }) {
  void mode;
  const { state, hooks } = useStore(LibraryTemplatePacksStore.instance);
  const { state: templatesState, hooks: templateHooks } = useStore(
    LibraryTemplatesStore.instance,
  );
  const { state: verticalsState, hooks: verticalHooks } = useStore(
    LibraryVerticalsStore.instance,
  );
  const { actions: appActions } = useStore(AppStore.instance);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<PackStatusFilter>("all");
  const [verticalFilter, setVerticalFilter] = useState("all");
  hooks.useTemplatePackStoreService({
    page: 1,
    page_size: 10,
  });
  templateHooks.useTemplateStoreService({
    page: 1,
    page_size: 100,
  });
  verticalHooks.useVerticalStoreService({
    page: 1,
    page_size: 100,
  });
  const closeDialog = useCallback(() => {
    appActions.dialog("close");
  }, [appActions]);
  const closeDrawer = useCallback(() => {
    appActions.rightDrawer("close");
  }, [appActions]);
  const packs = useMemo(
    () => state?.list.map((pack) => pack.getData()) ?? [],
    [state?.list],
  );
  const verticals = useMemo(
    () =>
      Array.from(
        new Set(
          packs.flatMap((pack) =>
            (pack.verticals ?? []).map(
              (vertical) => vertical.name ?? vertical.code ?? "",
            ),
          ),
        ),
      ).filter(Boolean),
    [packs],
  );
  const templateOptions = useMemo(
    () => templatesState?.list.map((template) => template.getData()) ?? [],
    [templatesState?.list],
  );
  const verticalOptions = useMemo(
    () => verticalsState?.list.map((vertical) => vertical.getData()) ?? [],
    [verticalsState?.list],
  );
  const filteredPacks = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return packs.filter((pack) => {
      const haystack = [
        pack.name,
        pack.description,
        pack.status,
        ...(pack.verticals ?? []).map((vertical) => vertical.name),
      ]
        .join(" ")
        .toLowerCase();

      return (
        (!normalizedSearch || haystack.includes(normalizedSearch)) &&
        (statusFilter === "all" || pack.status === statusFilter) &&
        (verticalFilter === "all" ||
          pack.verticals?.some((vertical) => vertical.name === verticalFilter))
      );
    });
  }, [packs, search, statusFilter, verticalFilter]);
  const openCreatePackDialog = useCallback(() => {
    appActions.dialog(
      "open",
      <DialogTemplate className="max-w-none">
        <CreateTemplatePackDialog
          onClose={closeDialog}
          packs={packs}
          templates={templateOptions}
          verticals={verticalOptions}
        />
      </DialogTemplate>,
    );
  }, [appActions, closeDialog, packs, templateOptions, verticalOptions]);
  const openPackDrawer = useCallback(
    (pack: APILibraryTemplatePackResponseType) => {
      appActions.rightDrawer(
        "open",
        620,
        <TemplatePackDetailDrawer onClose={closeDrawer} pack={pack} />,
      );
    },
    [appActions, closeDrawer],
  );
  const columns = useMemo(
    () => [
      {
        header: "Pack",
        key: "pack",
        render: (pack: APILibraryTemplatePackResponseType) => (
          <button
            className="max-w-xl text-left"
            onClick={() => openPackDrawer(pack)}
            type="button"
          >
            <div className="font-extrabold text-ink">{pack.name}</div>
            <div className="text-xs text-ink-muted">{pack.description}</div>
          </button>
        ),
      },
      {
        header: "Verticals",
        key: "verticals",
        render: (pack: APILibraryTemplatePackResponseType) => (
          <ChipList
            values={pack.verticals?.map(
              (vertical) => vertical.name ?? vertical.code,
            )}
          />
        ),
      },
      {
        header: "Status",
        key: "status",
        render: (pack: APILibraryTemplatePackResponseType) => (
          <StatusBadge status={pack.status} />
        ),
      },
      {
        header: "Templates",
        key: "templates",
        render: (pack: APILibraryTemplatePackResponseType) =>
          (pack.templates ?? []).length,
      },
      {
        className: "w-20",
        header: "",
        key: "action",
        render: (pack: APILibraryTemplatePackResponseType) => (
          <Button onClick={() => openPackDrawer(pack)} size="sm">
            Open
          </Button>
        ),
      },
    ],
    [openPackDrawer],
  );

  return (
    <>
      <PageHeader
        actions={
          <Button onClick={openCreatePackDialog} variant="primary">
            Create Template Pack
          </Button>
        }
        breadcrumb={["Library", "Template Packs"]}
        subtitle="Create bundles of templates for vertical onboarding. Plans allow packs; Tenant Setup installs them atomically."
        title="Template Packs"
      />
      <Banner tone="ok">
        <strong>Required for demo:</strong> Create Pack, edit metadata, select
        templates, activate and disable are functional.
      </Banner>
      <DataTable
        columns={columns}
        header={
          <AdminTableToolbar>
            <input
              className={`${adminInputClasses} w-80 max-w-full`}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search packs..."
              value={search}
            />
            <div className="flex flex-wrap items-center gap-2">
              <select
                className={adminSelectClasses}
                onChange={(event) =>
                  setStatusFilter(event.target.value as PackStatusFilter)
                }
                value={statusFilter}
              >
                <option value="all">All status</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
              <select
                className={adminSelectClasses}
                onChange={(event) => setVerticalFilter(event.target.value)}
                value={verticalFilter}
              >
                <option value="all">All verticals</option>
                {verticals.map((vertical) => (
                  <option key={vertical} value={vertical}>
                    {vertical}
                  </option>
                ))}
              </select>
            </div>
          </AdminTableToolbar>
        }
        rowKey={(pack) => String(pack.id ?? pack.url ?? pack.name ?? "")}
        rows={filteredPacks}
      />
    </>
  );
}
